import {workingSet} from './mergeContext';
import {isTaskLockedByFinalizedInvoice} from '../../Financial/Invoices/finalizedLock';
import store from '../store';
import Actions from '../actions/indexExport';
import {getCurrentUserId} from '../../lib/picos/currentLogin';
import newHRTimestamp from '../../lib/picos/timestamp';
import {findAll} from '../find';
import FlatFee from '../../entities/flatFee';
import Invoice from '../../entities/invoice';
import Task from '../../entities/task';
import FlatFeeMilestone from '../../entities/flatFeeMilestone';
import {canCascadeDelete, addDeleteImpact, deleteImpactContext, deleteImpactCounts, workingRows, enqueueDelete} from './deleteContext';
import type {DeleteImpact, DeleteImpactContext, DeleteResult, DeleteTraversal} from './deleteContext';
import {deleteFlatFeeMilestone, collectFlatFeeMilestoneDeleteImpact} from './flatFeeMilestoneDeps';
export function canDeleteFlatFee(): boolean {
return canCascadeDelete();
}
export function collectFlatFeeDeleteImpact(flatFee: FlatFee, context: DeleteImpactContext): void {
const key = 'flatFee:' + flatFee.id;
if (context.visited.has(key)) return;
context.visited.add(key);
const state = store.getState();
if (!!flatFee.invoiceId) {
addDeleteImpact(context, key, 'flatFees*');
return;
}
const invoiceFlatFeeIdsImpact = workingRows<Invoice>('invoice', findAll<Invoice>('invoices')).filter(invoiceRef => invoiceRef.flatFeeIds?.includes(flatFee.id) && !(invoiceRef.invoiceStatus === 'F'));
invoiceFlatFeeIdsImpact.forEach(invoiceRef => addDeleteImpact(context, 'invoice:' + invoiceRef.id, 'invoices'));
const taskFlatFeeBucketImpact = workingRows<Task>('task', findAll<Task>('tasks')).filter(taskRef => taskRef.flatFeeBucket === flatFee.id && !(isTaskLockedByFinalizedInvoice(taskRef, store.getState().invoices)));
taskFlatFeeBucketImpact.forEach(taskRef => addDeleteImpact(context, 'task:' + taskRef.id, 'tasks'));workingRows<FlatFeeMilestone>('flatFeeMilestone', findAll<FlatFeeMilestone>('flatFeeMilestones')).filter(flatFeeMilestone => flatFeeMilestone.status !== 'D' && flatFeeMilestone.entityType === 'flatFee' && flatFeeMilestone.entityId === flatFee.id).forEach(flatFeeMilestone => {
if (context.visited.has('flatFeeMilestone:' + flatFeeMilestone.id)) return;
addDeleteImpact(context, 'flatFeeMilestone:' + flatFeeMilestone.id, 'flatFeeMilestones');
collectFlatFeeMilestoneDeleteImpact(flatFeeMilestone, context);
});
}
export function deleteImpactFlatFee(flatFee: FlatFee): DeleteImpact {
const context = deleteImpactContext();
collectFlatFeeDeleteImpact(flatFee, context);
return deleteImpactCounts(context);
}
export function deleteFlatFee(flatFee: FlatFee, mergeId?: string, cascade?: DeleteTraversal): DeleteResult {
if (cascade && (!!flatFee.invoiceId)) return {status: 'blocked', impacts: [{value: 'flatFees*', count: 1}]};
if (!cascade) {
if (!canDeleteFlatFee()) return {status: 'notReady', impacts: []};
const context = deleteImpactContext();
collectFlatFeeDeleteImpact(flatFee, context);
if (context.blocked) return {status: 'blocked', impacts: deleteImpactCounts(context)};
cascade = {visited: new Set(), importSource: mergeId ? 'merge' : 'delete', importId: mergeId || flatFee.id};
}
const key = 'flatFee:' + flatFee.id;
if (cascade.visited.has(key)) return {status: 'deleted', impacts: []};
cascade.visited.add(key);
const targetImportSource = cascade.importSource;
mergeId = cascade.importId;
const _work = workingSet();
const dispatch = (action: any, key: string) => enqueueDelete(_work, onQueued => store.dispatch({...action, onQueued: (saved: boolean) => onQueued(saved, action.type.endsWith('_REMOVE_QUEUE'))}), () => _work.delete(key));
workingRows<FlatFeeMilestone>('flatFeeMilestone', findAll<FlatFeeMilestone>('flatFeeMilestones')).filter(flatFeeMilestone => flatFeeMilestone.status !== 'D' && flatFeeMilestone.entityType === 'flatFee' && flatFeeMilestone.entityId === flatFee.id).forEach(flatFeeMilestone => {deleteFlatFeeMilestone(flatFeeMilestone, mergeId, cascade);});
workingRows<Invoice>('invoice', [...store.getState().invoices.getMappingFor('flatFeeIds', flatFee.id)]).filter(invoiceTemp => invoiceTemp.flatFeeIds?.includes(flatFee.id)).forEach(invoiceTemp => {
const _invoice = ((_work.get('invoice:' + invoiceTemp.id) ?? invoiceTemp) as Invoice).clone();
if (_invoice.status === 'D') return;
if (_invoice.invoiceStatus === 'F') return;
_invoice.status = 'U';
_invoice.addedOn = newHRTimestamp();
_invoice.addedBy = getCurrentUserId();
_invoice.importSource = targetImportSource;
_invoice.importId = mergeId;
_invoice.flatFeeIds = _invoice.flatFeeIds?.filter(id => id !== flatFee.id) || [];

_work.set('invoice:' + _invoice.id, _invoice);
dispatch(Actions.InvoiceActions.saveInvoiceQueue(_invoice), 'invoice:' + _invoice.id);
});
workingRows<Task>('task', [...store.getState().tasks.getMappingFor('flatFeeBucket', flatFee.id)]).filter(taskTemp => taskTemp.flatFeeBucket === flatFee.id).forEach(taskTemp => {
const _task = ((_work.get('task:' + taskTemp.id) ?? taskTemp) as Task).clone();
if (_task.status === 'D') return;
if (isTaskLockedByFinalizedInvoice(_task, store.getState().invoices)) return;
_task.status = 'U';
_task.addedOn = newHRTimestamp();
_task.addedBy = getCurrentUserId();
_task.importSource = targetImportSource;
_task.importId = mergeId;
if (_task.flatFeeBucket === flatFee.id) {
_task.flatFeeBucket = null;
_task.flatFeeMilestoneBucket = null;
}
_work.set('task:' + _task.id, _task);
dispatch(Actions.TaskActions.saveTaskQueue(_task), 'task:' + _task.id);
});
const deleted = flatFee.clone();
deleted.status = 'D';
deleted.addedOn = newHRTimestamp();
deleted.addedBy = getCurrentUserId();
deleted.importSource = targetImportSource;
deleted.importId = mergeId;
_work.set(key, deleted);
const completion = dispatch(Actions.FlatFeeActions.removeFlatFeeQueue(deleted), key);
return {status: 'deleted', impacts: [], completion};
}

export function replaceFlatFee(flatFee: FlatFee, replaceWith: FlatFee, mergeId?: string) {
let targetImportSource = 'merge';
if (!mergeId) {targetImportSource = 'delete';mergeId = flatFee.id;}
const _work = workingSet();
workingRows<Invoice>('invoice', [...store.getState().invoices.getMappingFor('flatFeeIds', flatFee.id)]).filter(invoiceTemp => invoiceTemp.flatFeeIds?.includes(flatFee.id)).forEach(invoiceTemp => {
const _invoice = ((_work.get('invoice:' + invoiceTemp.id) ?? invoiceTemp) as Invoice).clone();
if (_invoice.status === 'D') return;
if (_invoice.invoiceStatus === 'F') return;
_invoice.status = 'U';
_invoice.addedOn = newHRTimestamp();
_invoice.addedBy = getCurrentUserId();
_invoice.importSource = targetImportSource;
_invoice.importId = mergeId;
_invoice.flatFeeIds = _invoice.flatFeeIds?.map(id => (id === flatFee.id) ? replaceWith.id : id) || [];

_work.set('invoice:' + _invoice.id, _invoice);
store.dispatch(Actions.InvoiceActions.saveInvoiceQueue(_invoice));
});
workingRows<Task>('task', [...store.getState().tasks.getMappingFor('flatFeeBucket', flatFee.id)]).filter(taskTemp => taskTemp.flatFeeBucket === flatFee.id).forEach(taskTemp => {
const _task = ((_work.get('task:' + taskTemp.id) ?? taskTemp) as Task).clone();
if (_task.status === 'D') return;
if (isTaskLockedByFinalizedInvoice(_task, store.getState().invoices)) return;
_task.status = 'U';
_task.addedOn = newHRTimestamp();
_task.addedBy = getCurrentUserId();
_task.importSource = targetImportSource;
_task.importId = mergeId;
if (_task.flatFeeBucket === flatFee.id) {
_task.flatFeeBucket = replaceWith.id;
_task.flatFeeMilestoneBucket = null;
}
_work.set('task:' + _task.id, _task);
store.dispatch(Actions.TaskActions.saveTaskQueue(_task));
});



workingRows<FlatFeeMilestone>('flatFeeMilestone', findAll<FlatFeeMilestone>('flatFeeMilestones')).filter(flatFeeMilestone => flatFeeMilestone.status !== 'D' && flatFeeMilestone.entityType === 'flatFee' && flatFeeMilestone.entityId === flatFee.id).forEach(flatFeeMilestoneTemp => {
const _flatFeeMilestone = ((_work.get('flatFeeMilestone:' + flatFeeMilestoneTemp.id) ?? flatFeeMilestoneTemp) as FlatFeeMilestone).clone();
if (_flatFeeMilestone.status === 'D') return;
_flatFeeMilestone.entityId = replaceWith.id;
_flatFeeMilestone.status = 'U';
_flatFeeMilestone.addedOn = newHRTimestamp();
_flatFeeMilestone.addedBy = getCurrentUserId();
_flatFeeMilestone.importSource = targetImportSource;
_flatFeeMilestone.importId = mergeId;
_work.set('flatFeeMilestone:' + _flatFeeMilestone.id, _flatFeeMilestone);
store.dispatch(Actions.FlatFeeMilestoneActions.saveFlatFeeMilestoneQueue(_flatFeeMilestone));
});
return flatFee;
}
